import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Space, Typography, Row, Col, Spin, Modal, Table } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import videoService from "../../services/videoService";
import masterService from "../../services/masterService";
import ImageUpload from "../../components/banners/ImageUpload";

const { Option } = Select;
const { Title, Text } = Typography;

const EditCategory = () => {
  const navigate = useNavigate();
  const { category_id } = useParams();
  const [form] = Form.useForm();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isAd, setIsAd] = useState("N");
  const [visible, setVisible] = useState(false);
  const [showsSpinner] = useState(false);

  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [seasonsOfSeries, setSeasonsOfSeries] = useState([]);

  const [showsList, setShowsList] = useState([]);
  const [showForm, setShowForm] = useState({
    show: null,
    show_type: null,
    season: null,
  });

  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setInitialLoading(true);
        // Load dropdowns and details in parallel (non-blocking)
        const [langs, tgs, cat, movies, series] = await Promise.allSettled([
          // Use the endpoint that returns the list reliably
          masterService.getVideoLanguages({
            perPage: 1000,
            currentPage: 1,
            searchText: "",
          }),
          masterService.getTagList({
            perPage: 1000,
            currentPage: 1,
            searchText: "",
          }),
          videoService.getSingleVideoCategory(category_id),
          masterService.getVideoShows(),
          masterService.getTvShows(),
        ]);

        if (langs.status === "fulfilled" && langs.value?.data?.status) {
          const langPayload = langs.value.data?.data;
          const langList = Array.isArray(langPayload)
            ? langPayload
            : langPayload?.data || [];
          setLanguages(langList);
        }
        if (tgs.status === "fulfilled" && tgs.value?.data?.status) {
          const tagPayload = tgs.value.data?.data;
          const tagList = Array.isArray(tagPayload)
            ? tagPayload
            : tagPayload?.data || [];
          setTags(tagList);
        }

        // Normalize API shape and log for debugging
        const apiData =
          cat.status === "fulfilled" ? cat.value?.data : undefined;
        console.log("EditCategory: getSingleVideoCategory response", apiData);
        console.log("EditCategory: category_id from params", category_id);
        let category = null;
        if (apiData && apiData.status) {
          // API returned successful response
          const payload = apiData.data !== undefined ? apiData.data : apiData;
          if (Array.isArray(payload)) {
            category = payload[0] || null;
          } else if (payload && typeof payload === "object") {
            category = payload;
          }
        } else if (cat.status === "rejected") {
          // API call failed
          console.error("EditCategory: API call rejected", cat.reason);
        } else if (apiData && !apiData.status) {
          // API returned error response
          console.error("EditCategory: API returned error", apiData.message || apiData);
        }

        if (movies.status === "fulfilled" && movies.value?.data?.status) {
          const mvPayload = movies.value.data?.data;
          const mvList = Array.isArray(mvPayload)
            ? mvPayload
            : mvPayload?.data || [];
          setVideoShows(Array.isArray(mvList) ? mvList : []);
        }
        if (series.status === "fulfilled" && series.value?.data?.status) {
          const tvPayload = series.value.data?.data;
          const tvList = Array.isArray(tvPayload)
            ? tvPayload
            : tvPayload?.data || [];
          setTvShows(Array.isArray(tvList) ? tvList : []);
        }

        if (!category) {
          // Fallback: fetch all and locate the record client-side (in case single endpoint differs)
          console.log("EditCategory: Primary API call didn't return category, trying fallback...");
          try {
            const alt = await videoService.getAllVideoCategory({
              perPage: 1000,
              currentPage: 1,
              searchText: "",
            });
            console.log("EditCategory: getAllVideoCategory fallback response", alt?.data);
            const all = alt?.data?.data || [];
            console.log("EditCategory: Searching for category_id:", category_id, "in array of", all.length, "items");
            const found = Array.isArray(all)
              ? all.find((c) => String(c.vc_id) === String(category_id))
              : null;
            if (found) {
              console.log("EditCategory: Found category in fallback", found);
              category = found;
            } else {
              console.error("EditCategory: Category not found in fallback. Available IDs:", all.map(c => c.vc_id));
              notify.error({ message: `Category not found (ID: ${category_id})` });
              navigate("/categories/category");
              return;
            }
          } catch (e) {
            console.error("EditCategory: Fallback API call failed", e);
            notify.error({ message: `Failed to fetch category: ${e.message || "Unknown error"}` });
            navigate("/categories/category");
            return;
          }
        }

        // Prefill form
        form.setFieldsValue({
          vc_name: category.vc_name,
          order: category.order,
          vc_description: category.vc_description,
          is_ad: category.is_ad || "N",
          vc_is_active: category.vc_is_active || "Y",
          home_page: category.home_page ?? "Y",
          movie_page: category.movie_page ?? "Y",
          series_page: category.series_page ?? "Y",
          lang_id: category.lang_id,
          tag_id: category.tag_id,
          ad_url: category.ad_url,
          card_image: category.card_image,
          banner_image: category.banner_image,
          vc_keywords: category.vc_keywords
            ? String(category.vc_keywords).split(',').map(s => s.trim()).filter(Boolean)
            : [],
        });
        setIsAd(category.is_ad || "N");
        // Prefill multilingual data if present
        if (Array.isArray(category.multilingual_data)) {
          setMultilingualData(
            category.multilingual_data.map((d) => ({
              lang_id: d.lang_id ?? null,
              vc_name: d.vc_name ?? "",
              vc_description: d.vc_description ?? "",
              vc_keywords: d.vc_keywords
                ? String(d.vc_keywords).split(',').map(s => s.trim()).filter(Boolean)
                : [],
            }))
          );
        }
        if (category && category.shows_list) {
          const list =
            typeof category.shows_list === "string"
              ? (() => {
                  try {
                    return JSON.parse(category.shows_list);
                  } catch {
                    return [];
                  }
                })()
              : category.shows_list;
          if (Array.isArray(list)) {
            // Normalize older payloads that used `video_show_title` instead of `show_title`
            const normalized = list.map((item) => ({
              ...item,
              show_title:
                item.show_title ||
                item.video_show_title ||
                item.tv_show_name ||
                "",
            }));
            setShowsList(normalized);
          }
        }
      } catch (error) {
        console.error("Error loading category:", error);
        notify.error({ message: "Failed to load category" });
        navigate("/categories/category");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [category_id, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      // Validate multilingual data
      const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
      if (new Set(langs).size !== langs.length) {
        notify.error({ message: "Duplicate languages in multilingual data" });
        setLoading(false);
        return;
      }
      for (let i = 0; i < multilingualData.length; i++) {
        const d = multilingualData[i];
        if (d.lang_id && (!d.vc_name || !d.vc_description)) {
          notify.error({ message: `Language entry #${i + 1}: Category name and description are required when language is selected` });
          setLoading(false);
          return;
        }
      }

      // serialize parent vc_keywords
      const parentSeo = Array.isArray(values.vc_keywords)
        ? values.vc_keywords.map((s) => String(s).trim()).filter(Boolean).join(',')
        : (values.vc_keywords || '');
      // serialize multilingual vc_keywords
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        vc_keywords: Array.isArray(d.vc_keywords)
          ? d.vc_keywords.map((s) => String(s).trim()).filter(Boolean).join(',')
          : (d.vc_keywords || ''),
      }));

      const payload = { ...values, vc_keywords: parentSeo, shows_list: showsList, multilingual_data: multilingualSerialized };
      const response = await videoService.editVideoCategory(
        category_id,
        payload
      );
      if (response?.data?.status) {
        notify.success({
          message: response?.data?.message || "Category updated successfully"
        });
        navigate("/categories/category");
      } else {
        notify.error({ message: response?.data?.message || "Failed to update category" });
      }
    } catch (error) {
      console.error("Error updating category:", error);
      notify.error({ message: "Failed to update category" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => navigate(-1);

  const handleNumberInput = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 37 &&
      charCode !== 39 &&
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  const handleAddShow = () => setVisible(true);

  const handleShowTypeChange = (value) => {
    setShowForm((prev) => ({
      ...prev,
      show_type: value,
      show: null,
      season: null,
    }));
    setSeasonsOfSeries([]);
  };

  const handleShowChange = async (value) => {
    setShowForm((prev) => ({ ...prev, show: value, season: null }));
    if (showForm.show_type === "series" && value) {
      try {
        const show = JSON.parse(value);
        const response = await masterService.getAllSeasonsOfTvShow({
          tv_show_id: show.tv_show_id,
        });
        if (response?.data?.status)
          setSeasonsOfSeries(response.data.data || []);
      } catch (err) {
        notify.error({ message: "Failed to fetch seasons" });
      }
    }
  };

  const handleSeasonChange = (value) =>
    setShowForm((prev) => ({ ...prev, season: value }));

  const handleSubmitShow = () => {
    if (!showForm.show) {
      notify.error({ message: "Please select a show" });
      return;
    }

    const showData = JSON.parse(showForm.show);
    const showId =
      showForm.show_type === "movie"
        ? showData.video_show_id
        : showData.tv_show_id;
    const showTitle =
      showForm.show_type === "movie"
        ? showData.video_show_title
        : showData.tv_show_name;

    let seasonId = null;
    let seasonTitle = null;
    if (showForm.season) {
      const season = JSON.parse(showForm.season);
      seasonId = season.season_id;
      seasonTitle = season.season_title;
    }

    const isDuplicate = showsList.some(
      (item) =>
        item.show_id === showId &&
        item.show_type === showForm.show_type &&
        item.season_id === seasonId
    );
    if (isDuplicate) {
      notify.error({ message: "This show is already added" });
      return;
    }

    const newShow = {
      show_id: showId,
      show_title: showForm.video_show_title || showTitle,
      show_type: showForm.show_type,
      season_id: seasonId,
      season_title: seasonTitle,
    };
    setShowsList((prev) => [...prev, newShow]);
    setShowForm({ show: null, show_type: null, season: null });
    setSeasonsOfSeries([]);
    setVisible(false);
  };

  const handleRemoveShow = (index) => {
    setShowsList((prev) => prev.filter((_, i) => i !== index));
  };

  const showsColumns = [
    {
      title: "Order",
      key: "order",
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Show Title",
      key: "show_title",
      render: (text, record) => {
        const title =
          record.show_title ||
          record.video_show_title ||
          record.tv_show_name ||
          "";
        return (
          <span>
            {title}
            {record.season_title ? ` - ${record.season_title}` : ""}
          </span>
        );
      },
    },
    {
      title: "Show Type",
      dataIndex: "show_type",
      key: "show_type",
      width: 120,
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (text, record, index) => (
        <Button type="link" danger onClick={() => handleRemoveShow(index)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card>
        <Spin spinning={initialLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              is_ad: "N",
              home_page: "Y",
              movie_page: "Y",
              series_page: "Y",
              vc_is_active: "Y",
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Category Name"
                  name="vc_name"
                  rules={[
                    { required: true, message: "Please enter category name" },
                  ]}
                >
                  <Input placeholder="Enter category name" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Order"
                  name="order"
                  rules={[
                    { required: true, message: "Please enter order" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "Order must be a non-negative integer",
                    },
                  ]}
                >
                  <Input
                    type="number"
                    placeholder="Enter order"
                    onKeyPress={handleNumberInput}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Category Description" name="vc_description">
              <Input.TextArea
                rows={4}
                placeholder="Enter category description"
              />
            </Form.Item>

            <Form.Item label="SEO Keywords" name="vc_keywords">
              <Select mode="tags" placeholder="Type a keyword and press Enter" tokenSeparators={[',']} open={false} />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Is Ad" name="is_ad">
                  <Select onChange={(val) => setIsAd(val)}>
                    <Option value="Y">Yes</Option>
                    <Option value="N">No</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Status" name="vc_is_active">
                  <Select>
                    <Option value="Y">Active</Option>
                    <Option value="N">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {isAd === "Y" && (
              <>
                <Form.Item label="Ad URL" name="ad_url">
                  <Input placeholder="Enter ad URL" />
                </Form.Item>

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <ImageUpload
                      label="Upload Card Image"
                      name="card_image"
                      width={300}
                      height={400}
                      imageType="video"
                      required={false}
                    />
                  </Col>
                  <Col span={12}>
                    <ImageUpload
                      label="Upload Banner Image"
                      name="banner_image"
                      width={1280}
                      height={720}
                      imageType="ads"
                      required={false}
                    />
                  </Col>
                </Row>
              </>
            )}

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Choose Language" name="lang_id">
                  <Select placeholder="Please select a language" allowClear>
                    {(Array.isArray(languages) ? languages : []).map(
                      (language) => (
                        <Option key={language.lang_id} value={language.lang_id}>
                          {language.lang_name}
                        </Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Tag" name="tag_id">
                  <Select placeholder="Please select Tag" allowClear>
                    {(Array.isArray(tags) ? tags : []).map((tag) => (
                      <Option key={tag.tag_id} value={tag.tag_id}>
                        {tag.tag_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Multilingual Data Section */}
            <Card style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>Multilingual Data</Title>
                <Button type="dashed" onClick={() => setMultilingualData([...multilingualData, { lang_id: null, vc_name: '', vc_description: '', vc_keywords: [] }])}>
                  Add Language
                </Button>
              </div>
              {multilingualData.length === 0 && (
                <div style={{ textAlign: 'center', padding: 12, background: '#fafafa' }}>
                  <Text type="secondary">No multilingual data added yet. Click "Add Language" to get started.</Text>
                </div>
              )}
              <div style={{ display: 'grid', gap: 12 }}>
                {multilingualData.map((data, index) => (
                  <Card
                    key={`ml-${index}`}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>
                          Language Entry #{index + 1}
                          {data.lang_id && ` - ${languages.find((l) => l.lang_id === data.lang_id)?.lang_name || 'Unknown Language'}`}
                        </Text>
                        <Button type="link" danger onClick={() => setMultilingualData(multilingualData.filter((_, i) => i !== index))}>
                          Remove
                        </Button>
                      </div>
                    }
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Form.Item label="Language" required style={{ marginBottom: 0 }}>
                        <Select
                          value={data.lang_id}
                          placeholder="Select language"
                          onChange={(value) => {
                            const arr = [...multilingualData];
                            arr[index].lang_id = value;
                            setMultilingualData(arr);
                          }}
                          showSearch
                          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                          {(Array.isArray(languages) ? languages : []).map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item label="Category Name" required style={{ marginBottom: 0 }}>
                        <Input
                          value={data.vc_name}
                          placeholder="Enter category name in this language"
                          onChange={(e) => {
                            const arr = [...multilingualData];
                            arr[index].vc_name = e.target.value;
                            setMultilingualData(arr);
                          }}
                        />
                      </Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                      <Form.Item label="Description" required style={{ marginBottom: 0 }}>
                        <Input.TextArea
                          rows={3}
                          value={data.vc_description}
                          placeholder="Enter category description in this language"
                          onChange={(e) => {
                            const arr = [...multilingualData];
                            arr[index].vc_description = e.target.value;
                            setMultilingualData(arr);
                          }}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item label="SEO Keywords" style={{ marginTop: 12 }}>
                      <Select
                        mode="tags"
                        value={Array.isArray(data.vc_keywords) ? data.vc_keywords : (data.vc_keywords ? String(data.vc_keywords).split(',').map(s => s.trim()).filter(Boolean) : [])}
                        placeholder="Type a keyword and press Enter"
                        onChange={(value) => {
                          const arr = [...multilingualData];
                          arr[index].vc_keywords = value;
                          setMultilingualData(arr);
                        }}
                        tokenSeparators={[',']}
                        open={false}
                      />
                    </Form.Item>
                  </Card>
                ))}
              </div>
            </Card>

            <Form.Item style={{ marginTop: "32px" }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                >
                  Update Category
                </Button>

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  size="large"
                >
                  Reset
                </Button>

                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleGoBack}
                  size="large"
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

      {isAd === "N" && (
        <Card style={{ marginTop: "24px" }}>
          <Spin spinning={showsSpinner}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Shows List
              </Title>
              <Button type="primary" onClick={handleAddShow}>
                Add Show
              </Button>
            </div>
            <Table
              columns={showsColumns}
              dataSource={showsList}
              rowKey={(record, index) => index}
              pagination={false}
              size="middle"
              locale={{ emptyText: "No shows added yet" }}
            />
          </Spin>
        </Card>
      )}

      <Modal
        title="Add Show"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={750}
      >
        <Form layout="vertical">
          <Form.Item label="Show Type">
            <Select
              placeholder="Please select show type"
              value={showForm.show_type}
              onChange={handleShowTypeChange}
            >
              <Option value="movie">Movie</Option>
              <Option value="series">Series</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Select Show">
            {showForm.show_type === "movie" && (
              <Select
                placeholder="Please select movie"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {(Array.isArray(videoShows) ? videoShows : []).map((show) => (
                  <Option key={show.video_show_id} value={JSON.stringify(show)}>
                    {show.video_show_title}
                  </Option>
                ))}
              </Select>
            )}
            {showForm.show_type === "series" && (
              <Select
                placeholder="Please select series"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {(Array.isArray(tvShows) ? tvShows : []).map((show) => (
                  <Option key={show.tv_show_id} value={JSON.stringify(show)}>
                    {show.tv_show_name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          {showForm.show_type === "series" && (
            <Form.Item label="Select Season">
              <Select
                placeholder="Please select season"
                value={showForm.season}
                onChange={handleSeasonChange}
                allowClear
              >
                <Option value={null}>No Season</Option>
                {seasonsOfSeries.map((season) => (
                  <Option key={season.season_id} value={JSON.stringify(season)}>
                    {season.season_title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: "24px", textAlign: "right" }}>
            <Space>
              <Button onClick={() => setVisible(false)}>Cancel</Button>
              <Button type="primary" onClick={handleSubmitShow}>
                Add Show
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EditCategory;
